import { Group } from "@fairfleet/geotab";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { GroupColumn as GroupColumnData } from "../utils/groupTree";
import { ShowMoreButton } from "@/components/ShowMoreButton";
import { LIST_PAGE_SIZE } from "@/constants/renderLimits";
import { useShowMore } from "@/hooks/useShowMore";

type GroupNodeCardProps = {
  label: string;
  selected?: boolean;
  compact?: boolean;
  onClick?: () => void;
};

function GroupNodeCard({ label, selected, compact, onClick }: GroupNodeCardProps) {
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        px: 2,
        height: compact ? 34 : 40,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderColor: selected ? "primary.main" : "divider",
        backgroundColor: selected ? "primary.main" : "background.paper",
        color: selected ? "primary.contrastText" : "text.primary",
        borderRadius: 1,
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: selected ? 600 : 500 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.9 }}>
        {selected ? "▸" : "▾"}
      </Typography>
    </Paper>
  );
}

function GroupColumn({
  title,
  builtIn,
  groups,
  selectedBuiltIn,
  selectedGroup,
  onSelect,
  showBuiltIn,
}: {
  title: string;
  builtIn: Group[];
  groups: Group[];
  selectedBuiltIn?: string;
  selectedGroup?: string;
  onSelect: (groupId: string) => void;
  showBuiltIn: boolean;
}) {
  const { t } = useTranslation();
  // A parent can hold thousands of children; the column is keyed by its parent, so a new parent starts at page one.
  const { visible, hiddenCount, showMore } = useShowMore(groups, LIST_PAGE_SIZE, selectedGroup);

  return (
    <Stack spacing={1.25} sx={{ minWidth: 240 }}>
      <Typography variant="body2" color="text.primary" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>

      {showBuiltIn && builtIn.length > 0 ? (
        <Paper variant="outlined" sx={{ p: 1.25, backgroundColor: "#f9fbff" }}>
          <Typography variant="caption" color="primary.main" sx={{ fontWeight: 700 }}>
            {t("groupsPage.builtInGroups")}
          </Typography>
          <Divider sx={{ my: 0.75 }} />
          <Stack spacing={1}>
            {builtIn.map(group => (
              <GroupNodeCard
                key={group.id}
                label={group.name ?? group.id}
                selected={selectedBuiltIn === group.name || selectedGroup === group.id}
                onClick={() => onSelect(group.id)}
              />
            ))}
          </Stack>
        </Paper>
      ) : null}

      <Paper variant="outlined" sx={{ p: 1.25, backgroundColor: "#ffffff" }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
          {t("groupsPage.groups")}
        </Typography>
        <Divider sx={{ my: 0.75 }} />
        <Stack spacing={1}>
          {visible.map(group => (
            <GroupNodeCard
              key={group.id}
              label={group.name ?? group.id}
              compact
              selected={selectedGroup === group.id}
              onClick={() => onSelect(group.id)}
            />
          ))}
          <ShowMoreButton hiddenCount={hiddenCount} onClick={showMore} />
        </Stack>
      </Paper>
    </Stack>
  );
}

type Props = {
  companyGroup: Group;
  columns: GroupColumnData[];
  pathGroups: Group[];
  selectedGroupId: string;
  isMobile: boolean;
  onSelectCompany: () => void;
  onSelect: (depth: number, groupId: string) => void;
};

export default function GroupTreeColumns({
  companyGroup,
  columns,
  pathGroups,
  selectedGroupId,
  isMobile,
  onSelectCompany,
  onSelect,
}: Props) {
  const { t } = useTranslation();
  const [showBuiltIn, setShowBuiltIn] = useState(true);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        backgroundColor: "#f5f6f8",
        overflowX: "auto",
        minHeight: 560,
        position: "relative",
        // Fill the space next to the edit panel; minWidth 0 lets it shrink so wide trees
        // scroll inside this panel instead of widening the page.
        boxSizing: "border-box",
        flex: isMobile ? "1 1 auto" : "1 1 0",
        minWidth: 0,
      }}
    >
      <Stack direction="row" justifyContent="flex-end" sx={{ pb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={showBuiltIn}
              onChange={(_event, checked) => setShowBuiltIn(checked)}
              size="small"
            />
          }
          label={t("groupsPage.showBuiltInGroups")}
          sx={{ mr: 0 }}
        />
      </Stack>

      <Stack
        direction={isMobile ? "column" : "row"}
        spacing={3}
        alignItems="flex-start"
        sx={{ position: "relative", minWidth: isMobile ? "auto" : 760 }}
      >
        <Stack spacing={1} sx={{ minWidth: 220, pt: isMobile ? 0 : 27 }}>
          <GroupNodeCard
            label={companyGroup.name ?? t("groupsPage.companyGroup")}
            selected={selectedGroupId === companyGroup.id}
            onClick={onSelectCompany}
          />
        </Stack>

        {columns.map((column, index) => (
          <GroupColumn
            key={column.parent.id}
            title={column.parent.name ?? t("groupsPage.companyGroup")}
            builtIn={column.builtIn}
            groups={column.groups}
            selectedBuiltIn={pathGroups[index + 1]?.name}
            selectedGroup={pathGroups[index + 1]?.id}
            onSelect={groupId => onSelect(index, groupId)}
            showBuiltIn={showBuiltIn}
          />
        ))}
      </Stack>
    </Paper>
  );
}
